<template>
  <div class="w-full flex flex-col gap-3" :class="{'cursor-pointer': isDragging}">
    <div class="w-full flex" :style="{ height: `${GRAPH_HEIGHT}px` }">
      <!-- Y Axis Labels -->
      <div class="w-8 h-full flex flex-col-reverse justify-between mr-3">
        <BaseText v-for="label in yAxisLabels" :key="`x-axis-label-${label}`" 
        type="body" size="xs" class="text-text-subdued">
          {{ label }}
        </BaseText>
      </div>
      <!-- Graph Area -->
      <div class="relative flex-grow h-full">
        <!-- Plot lines -->
        <div class="absolute top-0 left-0 w-full h-full flex flex-col justify-between py-2">
          <div v-for="n in 5" :key="`plot-line-${n}`" class="w-full h-px bg-border-normal" />
        </div>
        <!-- Line Renderer -->
        <div class="absolute top-2 bottom-2 left-0 right-0 z-50">
          <div id="retention-line-graph" class="pointer-events-none" />
        </div>
      </div>
    </div>
    <div ref="progressBarContainer" class="w-full flex flex-col gap-3" style="padding-left: 44px;">
      <div v-if="advertisement?.video_url_source" class="relative w-full h-3 flex items-center cursor-pointer" @click="barClick">
        <div class="w-full h-0 border-t-2 border-neutral-25" />
        <div
          ref="progressBar"
          class="absolute top-0 left-0 h-full" 
          :style="{ width: `calc(100% - ${scaleOffsetPx}px)` }"
        >
          <div class="relative h-full flex items-center" :style="{ width: `${progress}%` }">
            <div class="w-full h-0 z-50 rounded-sm" style="border-top: 2px solid #0097FF" />
            <div 
              class="progress-scrubber" :class="{'dragging': isDragging}" 
              @mousedown="scrubberMouseDown"
              @mouseup="scrubberMouseUp"
              @mousemove="scrubberMouseMove"
              draggable="false"
              @dragstart.prevent
            />
          </div>
        </div>
      </div>
      <!-- Timestamp Labels -->
      <div class="w-full flex items-center justify-between">
        <div class="relative w-px h-4" v-for="label in timestampLabels" :key="`time-${label}`">
          <BaseText type="body" size="xs" class="absolute left-1/2 transform -translate-x-1/2 whitespace-nowrap transition-colors duration-200"
          :class="highlightLabel === label ? 'text-text-normal' : 'text-text-subdued'">
            {{ label }}
          </BaseText>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApexCharts from 'apexcharts'
import { scaleLinear } from 'd3-scale'

const GRAPH_HEIGHT = 120

export default {
  name: 'RetentionLineGraph',
  props: {
    advertisement: {
      type: Object,
      required: true
    },
    retentionSeries: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      // Graph State
      yAxisLabels: ['0%', '25%', '50%', '75%', '100%'],
      scaleOffsetPx: 0,
      GRAPH_HEIGHT,

      // Progress Bar State
      progress: 0,
      currentTime: 0,
      timestamps: [],
      isDragging: false,
      timeUpdateBuffering: false
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.intializeLineGraph(this.retentionSeries)
    })
  },
  beforeDestroy () {
    document.removeEventListener('mouseup', this.scrubberMouseUp)
    document.removeEventListener('mousemove', this.scrubberMouseMove)
  },
  computed: {
    videoDuration () {
      return Math.round(this.advertisement.video_duration)
    },
    timestampLabels () {
      return this.timestamps.map(time => this.formatTime(time))
    },
    highlightLabel () {
      const nearestTime = this.timestamps.find(time => Math.abs(time - this.currentTime) < 1)
      if (!nearestTime) return null
      return this.formatTime(nearestTime)
    }
  },
  methods: {
    intializeLineGraph (series) {
      this.computeTimestampLabels()

      const options = {
        series: [{
          name: 'Audience Retention',
          data: series
        }],
        colors: ['#0097FF'],
        chart: {
          type: 'area',
          height: '100%',
          toolbar: { show: false },
          zoom: { enabled: false },
          offsetY: 0,
          offsetX: 0,
          sparkline: { enabled: true }
        },
        stroke: {
          width: 1.5,
          lineCap: 'round'
        },
        fill: {
          type: 'gradient',
          gradient: {
            type: 'vertical',
            gradientToColors: ['#0097FF'],
            opacityFrom: 0.4,
            opacityTo: 0.1,
          }
        },
        grid: {
          show: false,
          padding: { left: 0, right: this.scaleOffsetPx, top: 0, bottom: 0 }
        },
        dataLabels: { enabled: false },
        tooltip: { enabled: false },
        legend: { enabled: false },
        yaxis: {
          show: false,
          min: 0,
          max: 100,
          forceNiceScale: false
        },
        xaxis: { type: 'numeric', labels: { show: false } }
      }
      this.$nextTick(() => {
        const chartContainer = document.getElementById('retention-line-graph')
        this.chart = new ApexCharts(chartContainer, options)
        this.chart.render()
      })
    },
    computeTimestampLabels () {
      const scale = scaleLinear().domain([0, this.videoDuration]).nice(6)
      this.timestamps = scale.ticks(6)

      // Compute the right offset of the graph so video duration aligns with the labels
      const totalWidth = this.$refs.progressBarContainer.clientWidth
      const segWidth = totalWidth / (this.timestamps.length - 1)
      const stepSecs = this.timestamps[1]
      const maxLabelDurDiff = this.timestamps[this.timestamps.length - 1] - this.videoDuration

      const offsetPx = Math.max(0, segWidth * (maxLabelDurDiff / stepSecs))
      this.scaleOffsetPx = offsetPx
    },
    formatTime (seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.floor(seconds % 60)
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    },
    // Video Player Methods
    updateProgress (currentTime, isManual = false) {
      if (!isManual && this.isDragging) return
      if (this.videoDuration > 0) {
        this.progress = Math.max(0, Math.min(100, (currentTime / this.videoDuration) * 100))
        this.currentTime = currentTime
      }
    },
    barClick (event) {
      if (this.isDragging) return
      const barRect = this.$refs.progressBar.getBoundingClientRect()
      const clickX = event.clientX - barRect.left
      const newTime = (clickX / barRect.width) * this.videoDuration
      this.updateProgress(newTime, true)
      this.$emit('updatePlayerTime', newTime)
    },
    scrubberMouseDown (event) {
      this.isDragging = true
      document.addEventListener('mouseup', this.scrubberMouseUp)
      document.addEventListener('mousemove', this.scrubberMouseMove)
    },
    scrubberMouseUp () {
      this.isDragging = false
      this.$emit('updatePlayerTime', this.currentTime)
      document.removeEventListener('mouseup', this.scrubberMouseUp)
      document.removeEventListener('mousemove', this.scrubberMouseMove)
    },
    scrubberMouseMove (event) {
      if (!this.isDragging) return
      const barRect = this.$refs.progressBar.getBoundingClientRect()
      const clickX = event.clientX - barRect.left
      const newTime = (clickX / barRect.width) * this.videoDuration
      this.updateProgress(newTime, true)
      if (!this.timeUpdateBuffering) {
        this.timeUpdateBuffering = true
        this.$emit('updatePlayerTime', newTime)
        setTimeout(() => {
          this.timeUpdateBuffering = false
        }, 100)
      }
    }
  }
}
</script>

<style scoped>
.progress-scrubber {
  position: absolute;
  top: 0;
  right: -6px;
  width: 12px;
  height: 12px;
  border: 2px solid #0097FF;
  border-radius: 50%;
  background-color: white;
  z-index: 100;
  transform-origin: center;
  transition: transform 0.1s ease-in-out;

  user-select: none;
  -webkit-user-drag: none;
  -moz-user-drag: none;
  -ms-user-drag: none;
}
.progress-scrubber.dragging, .progress-scrubber:hover {
  transform: scale(1.2);
}
</style>